import type { CableEngineeringWarning } from "../services/cable";
import { StatusIcon } from "./StatusIcon";
import type { StatusTone } from "./statusTone";

type WarningCode = CableEngineeringWarning["code"];

interface CableWarningPanelProps {
  warnings: CableEngineeringWarning[];
}

const NO_WARNINGS_TEXT = "No engineering warnings raised.";

// Exhaustive map: a new warning code without a label is a compile error.
const WARNING_CODE_LABELS: Record<WarningCode, string> = {
  AMPACITY_INADEQUATE: "Ampacity inadequate",
  VOLTAGE_DROP_EXCEEDED: "Voltage drop exceeded",
  SHORT_CIRCUIT_WITHSTAND_INADEQUATE: "Short-circuit withstand inadequate",
  NEUTRAL_SIZE_INADEQUATE: "Neutral size inadequate",
  PROTECTIVE_CONDUCTOR_INADEQUATE: "Protective conductor inadequate",
  HIGH_TOTAL_DERATING: "High total derating",
  PARALLEL_CABLE_CURRENT_SHARING: "Parallel cable current sharing",
  SOIL_DATA_REQUIRED: "Soil data required",
  AMBIENT_DERATING_NOT_ESTABLISHED: "Ambient derating factor not established",
  GROUPING_DERATING_NOT_ESTABLISHED: "Grouping derating factor not established",
  DERATING_FACTOR_NOT_ESTABLISHED: "Derating factor not established - engineering review required",
  GOVERNING_REFERENCE_NOT_ESTABLISHED:
    "Governing references not registered for this jurisdiction profile",
  GOVERNING_REFERENCE_OVERRIDDEN:
    "Governing references overridden by the project (deviation from profile)",
  NO_STANDARD_SIZE_AVAILABLE: "No standard size available",
};

// The tone of each warning (A25 (d)), exhaustive so a new code must be placed: an
// inadequate conductor or no size that fits is a failed check; a factor or reference not
// established is a review the engineer owes; the rest are plain warnings.
const WARNING_CODE_TONES: Record<WarningCode, StatusTone> = {
  AMPACITY_INADEQUATE: "fail",
  VOLTAGE_DROP_EXCEEDED: "fail",
  SHORT_CIRCUIT_WITHSTAND_INADEQUATE: "fail",
  NEUTRAL_SIZE_INADEQUATE: "fail",
  PROTECTIVE_CONDUCTOR_INADEQUATE: "fail",
  NO_STANDARD_SIZE_AVAILABLE: "fail",
  HIGH_TOTAL_DERATING: "warning",
  PARALLEL_CABLE_CURRENT_SHARING: "warning",
  SOIL_DATA_REQUIRED: "warning",
  AMBIENT_DERATING_NOT_ESTABLISHED: "review",
  GROUPING_DERATING_NOT_ESTABLISHED: "review",
  DERATING_FACTOR_NOT_ESTABLISHED: "review",
  GOVERNING_REFERENCE_NOT_ESTABLISHED: "review",
  GOVERNING_REFERENCE_OVERRIDDEN: "warning",
};

export function CableWarningPanel({ warnings }: CableWarningPanelProps) {
  return (
    <section aria-label="Engineering warnings">
      <h3>Engineering warnings</h3>
      {warnings.length === 0 ? (
        <p data-no-warnings="true">{NO_WARNINGS_TEXT}</p>
      ) : (
        <ul data-warning-count={warnings.length}>
          {warnings.map((warning, index) => (
            <li
              key={`${warning.code}-${index}`}
              data-warning-code={warning.code}
              data-warning-tone={WARNING_CODE_TONES[warning.code]}
            >
              <strong>
                <StatusIcon tone={WARNING_CODE_TONES[warning.code]} size={16} />
                {WARNING_CODE_LABELS[warning.code]}
              </strong>
              {": "}
              <span>{warning.message}</span>
              {warning.field_name ? (
                <>
                  {" "}
                  <small data-warning-field={warning.field_name}>
                    (Field: <code>{warning.field_name}</code>)
                  </small>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export { WARNING_CODE_TONES };

export default CableWarningPanel;
